/**
 * Reglas del "tipo" de un código de referido: qué se puede escribir en el
 * campo y cómo lo transforma el servidor.
 *
 * este archivo DUPLICA la lógica que aplica el backend
 * (`apireferidos`, POST /api/referral/generate) antes de guardar el código.
 * Existe para poder filtrar la entrada del campo en el momento.
 * Si las reglas cambian en el backend, hay que actualizarlas también aquí.
 *
 * Nada de esto es la fuente de verdad: en cuanto llega la respuesta de la API,
 * la interfaz muestra el `code` / `code_type` que devolvió el servidor.
 *
 * Transformaciones que aplica el backend, en este orden:
 *   1. Se quitan los espacios de los extremos.
 *   2. Todo pasa a mayúsculas.
 *   3. Se eliminan los acentos y diacríticos (café → CAFE, niño → NINO).
 *   4. Cada espacio se convierte en guion bajo (promo verano → PROMO_VERANO).
 *   5. Se descarta cualquier carácter que no sea A-Z, 0-9, "_" o "-".
 *   6. El resultado se recorta a 20 caracteres.
 *
 * El código final es ese valor más un sufijo aleatorio que pone el servidor
 * (PROMO_VERANO-A1B2, por ejemplo).
 */

// Longitud máxima que acepta el backend para el tipo, ya saneado.
const MAX_CODE_TYPE_LENGTH = 20;

// Único mensaje de error del campo. Solo puede aparecer cuando se escribe algo
// que no deja ninguna letra ni número (por ejemplo, solo espacios): el resto de
// caracteres inválidos ya no llegan a escribirse.
export const INVALID_CODE_TYPE_MESSAGE =
  "Escribe al menos una letra o un número.";

// Mismas reglas que el backend, sin recortar: sirve para medir la longitud
// real que va a contar el servidor.
const applySanitizeRules = (rawType) => {
  if (!rawType) return "";

  return String(rawType)
    .trim()
    .toUpperCase()
    // Separa cada letra de su acento y elimina los acentos sueltos.
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Cualquier espacio en blanco (espacio, tabulador…) pasa a guion bajo.
    .replace(/\s/g, "_")
    // Solo sobreviven letras sin acento, números, guion bajo y guion medio.
    .replace(/[^A-Z0-9_-]/g, "");
};

// Un carácter se puede escribir si sobrevive al saneamiento del backend: se
// comprueba ya sin acentos y en mayúscula, de forma que "é" y "ñ" se aceptan
// aunque el servidor los guarde como "E" y "N". Los emojis, símbolos y signos
// de puntuación no pasan.
const isTypeableChar = (char) =>
  /^[A-Z0-9_ -]$/.test(
    char
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
  );

/**
 * Deja en el campo solo lo que se puede escribir y corta en cuanto se alcanza
 * el límite de 20 caracteres del texto ya saneado.
 *
 * El recorrido es por "code points" (`for...of` sobre la cadena), no por
 * posiciones: así un emoji formado por varios code points (banderas, tonos de
 * piel, uniones con ZWJ) se elimina entero y no deja restos.
 */
export const filterCodeTypeInput = (value) => {
  if (!value) return "";

  let result = "";

  for (const char of String(value)) {
    if (!isTypeableChar(char)) continue;

    const candidate = result + char;

    // El límite se mide sobre el texto saneado, que es lo que cuenta el
    // backend: los espacios de los extremos, por ejemplo, no ocupan.
    if (applySanitizeRules(candidate).length > MAX_CODE_TYPE_LENGTH) break;

    result = candidate;
  }

  return result;
};

/**
 * Indica si el texto del campo deja algo utilizable, es decir, si se puede
 * generar el código con él. Solo es falso con el campo vacío o con texto que no
 * deja ninguna letra ni número, por ejemplo solo espacios.
 */
export const isUsableCodeType = (value) =>
  applySanitizeRules(value).length > 0;

/**
 * Enlace de referido que se copia y se comparte. Siempre se construye con el
 * `code` que devolvió el servidor, nunca con lo que escribió el usuario.
 */
export const buildReferralUrl = (code) =>
  `https://chateapro.app/register/?ref=${code}`;
